import React, { forwardRef, useRef, useState } from 'react'

import { useTranslation } from 'react-i18n-lite'

import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import {
  set,
  isValid,
  startOfDay,
  roundToNearestMinutes,
  getYear,
  getMonth,
  getHours,
  getMinutes,
  addMinutes,
  getDate,
  setMinutes
} from 'date-fns'

import { enUS } from 'date-fns/locale'

import { SelectionMenuOption } from '~/components/Selections/SelectionMenu'

import useClickOutside from '~/hooks/useClickOutside'
import useIsOpen from '~/hooks/useIsOpen'
import useMask from '~/hooks/useMask'

import formatDateTime, {
  AllowedLocales,
  localePatterns
} from '~/utils/date/formatDateTime'

import Button from '../../Buttons/Button'
import Icon from '../../Icon'

import Input from '../Input'

import CustomHeader from './components/CustomHeader'
import TimeSelect from './components/TimeSelect'

import {
  Container,
  DatePickerFooter,
  DatePickerStructure,
  DatePickerIcon
} from './styles'

import { DatePickerProps } from './type'

export const CURRENT_YEAR = getYear(new Date())

const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      heightClass = 'regular',
      disabledDates = [],
      minimumYear = CURRENT_YEAR,
      maximumYear = CURRENT_YEAR,
      format = 'date',
      ...props
    },
    ref
  ) => {
    const [selectedDate, setSelectedDate] = useState<Date>(
      roundToNearestMinutes(new Date(), { nearestTo: 15 })
    )

    const { isOpen, open, close } = useIsOpen()

    const { t, language } = useTranslation()

    const { applyMask } = useMask()

    const containerRef = useRef(null)

    const intlLocale = t('intl-locale') as AllowedLocales

    useClickOutside(containerRef, close)

    const maskByLocale = localePatterns[intlLocale].replace(/0/gi, '#')

    const maskByFormat = {
      time: maskByLocale.split(' | ')[1],
      date: maskByLocale.split(' | ')[0],
      'date-time': maskByLocale
    }

    const mask = maskByFormat[format]

    const base = startOfDay(new Date())

    const minutesInDay = 1440

    const interval = 15

    const times = Array.from(
      { length: minutesInDay / interval },
      (_, index) => {
        const minutes = index * interval

        const currentDate = addMinutes(base, index * interval)

        const formattedTime = formatDateTime(currentDate, intlLocale, 'time')

        return {
          id: formattedTime,
          value: minutes.toString(),
          label: formattedTime
        }
      }
    )

    const getSelectedTime = () => {
      const value = formatDateTime(selectedDate, intlLocale, 'time')

      return times.find((time) => time.id === value) ?? times[0]
    }

    const handleChange = (value: string) => {
      const currentDate = new Date()

      const formattedValue = applyMask(value, mask)

      const [dates, times] = formattedValue.split(' | ')

      const splittedDate = dates.split('/')

      const date =
        parseInt(splittedDate[intlLocale === 'en-US' ? 1 : 0]) ||
        getDate(currentDate)

      const month =
        parseInt(splittedDate[intlLocale === 'en-US' ? 0 : 1]) - 1 ||
        getMonth(currentDate)

      const year = parseInt(splittedDate[2]) || getYear(currentDate)

      const splittedTime = times?.split(':') || []

      const hours = parseInt(splittedTime[0]) || getHours(currentDate)

      const minutes = parseInt(splittedTime[1]) || getMinutes(currentDate)

      const newDate = roundToNearestMinutes(
        set(currentDate, { date, month, year, hours, minutes }),
        { nearestTo: 15 }
      )

      onChange(formattedValue)

      if (!isValid(newDate)) {
        return
      }

      setSelectedDate(newDate)
    }

    const handleTimeChange = ({ value }: SelectionMenuOption) => {
      setSelectedDate(setMinutes(startOfDay(selectedDate), parseInt(value)))
    }

    const handleClick = () => {
      const formattedValue = formatDateTime(selectedDate, intlLocale, format)

      onChange(formattedValue)
      close()
    }

    const handleIconClick = () => {
      isOpen ? close() : open()
    }

    const rightAreaElement = () => (
      <DatePickerIcon
        disabled={props.disabled || props.informational}
        onClick={handleIconClick}
        type='button'
        heightClass={heightClass}
      >
        <Icon iconName='Calendar' icSize='small' icColor='sanJuan' />
      </DatePickerIcon>
    )

    const renderCustomHeader = ({
      changeMonth,
      changeYear,
      ...params
    }: {
      changeMonth: (value: number) => void
      changeYear: (value: number) => void
    }) => {
      const changeCustomMonth = (value: number) => {
        selectedDate.setMonth(value)

        changeMonth(value)

        setSelectedDate(selectedDate)
      }

      const changeCustomYear = (value: number) => {
        selectedDate.setFullYear(value)

        changeYear(value)

        setSelectedDate(selectedDate)
      }

      return CustomHeader({
        ...params,
        changeYear: changeCustomYear,
        changeMonth: changeCustomMonth,
        language,
        minimumYear,
        maximumYear
      })
    }

    return (
      <Container ref={containerRef}>
        <Input
          value={value}
          onChange={handleChange}
          placeholder={formatDateTime(
            new Date('1969-10-29T12:30:00.000Z'),
            intlLocale,
            format
          )}
          rightAreaElement={rightAreaElement}
          heightClass={heightClass}
          {...props}
          ref={ref}
        />

        {isOpen && (
          <DatePickerStructure role='dialog' heightClass={heightClass}>
            {format !== 'time' && (
              <ReactDatePicker
                selected={selectedDate}
                onChange={(date: Date) => setSelectedDate(date)}
                excludeDates={disabledDates}
                renderCustomHeader={renderCustomHeader}
                shouldCloseOnSelect={false}
                locale={enUS}
                inline
              />
            )}

            <DatePickerFooter>
              {format !== 'date' && (
                <TimeSelect
                  times={times}
                  time={getSelectedTime()}
                  setTime={handleTimeChange}
                />
              )}

              <Button onClick={handleClick}>{t('datepicker.select')}</Button>
            </DatePickerFooter>
          </DatePickerStructure>
        )}
      </Container>
    )
  }
)

DatePicker.displayName = 'DatePicker'

export default DatePicker
